import React from 'react';
import { Apple, Play, Monitor, Globe, Download as DL, Check, Smartphone, Car } from 'lucide-react';
import { toast } from 'sonner';
import { detectOS, osLabel } from '../lib/detectOS';

const PLATFORMS = [
  {
    id: 'ios', name: 'iOS', icon: Apple, sub: 'iPhone & iPad · iOS 15+',
    badge: 'Coming Soon', status: 'coming',
    action: { type: 'waitlist', label: 'Notify me' },
    url: 'itms-apps://itunes.apple.com/app/id0000000',
  },
  {
    id: 'android', name: 'Android', icon: Play, sub: 'Android 8.0+ · Google Play',
    badge: 'Coming Soon', status: 'coming',
    action: { type: 'waitlist', label: 'Notify me' },
    url: 'https://play.google.com/store/apps/details?id=direct.automobile.app',
  },
  {
    id: 'windows', name: 'Windows', icon: Monitor, sub: 'Windows 10/11 · 64-bit',
    badge: 'Beta', status: 'beta',
    action: { type: 'download', label: 'Download .exe (89 MB)' },
    url: '/downloads/AutoMobile-Setup-1.0.0-beta.exe',
  },
  {
    id: 'pwa', name: 'Web / PWA', icon: Globe, sub: 'Works on any browser or vehicle infotainment',
    badge: 'Available Now', status: 'available',
    action: { type: 'install', label: 'Install PWA' },
    url: '/',
  },
];

function BadgeColor(status) {
  return {
    available: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    beta: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    coming: 'bg-zinc-700/40 text-zinc-400 border-zinc-600/40',
  }[status];
}

export default function Download() {
  const [os] = React.useState(detectOS());
  const [email, setEmail] = React.useState('');
  const [installEvt, setInstallEvt] = React.useState(null);

  React.useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleAction = async (p) => {
    if (p.action.type === 'download') {
      // Trigger placeholder download (file will 404 gracefully in dev — CF Pages will serve)
      const a = document.createElement('a');
      a.href = p.url;
      a.download = '';
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`Downloading AutoMobile for ${p.name}`);
    } else if (p.action.type === 'install') {
      if (installEvt) {
        installEvt.prompt();
        const { outcome } = await installEvt.userChoice;
        if (outcome === 'accepted') toast.success('AutoMobile installed!');
        setInstallEvt(null);
      } else {
        toast.info('Use your browser menu → "Install app" to add AutoMobile to your device.');
      }
    } else if (p.action.type === 'waitlist') {
      if (!email || !/\S+@\S+\.\S+/.test(email)) {
        toast.error('Enter a valid email above first');
        return;
      }
      toast.success(`You're on the ${p.name} waitlist. We'll email ${email}.`);
      setEmail('');
    }
  };

  return (
    <div data-testid="page-download">
      {/* Hero */}
      <section className="relative border-b border-white/5">
        <div className="am-grain absolute inset-0"/>
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="font-mono text-xs uppercase tracking-widest text-[#ff2a2a] mb-3">Download AutoMobile</div>
            <h1 className="font-display text-5xl sm:text-6xl uppercase tracking-tight leading-none">
              One garage. <br/><span className="text-[#ff2a2a]">Every device.</span>
            </h1>
            <p className="mt-4 text-zinc-400 max-w-xl">
              Native apps for iOS and Android, a Windows installer, or install the PWA directly to your phone or vehicle&rsquo;s infotainment. Free forever.{' '}
            </p>
            <div className="mt-6 inline-flex items-center gap-2 hairline px-3 py-2 text-xs font-mono uppercase tracking-widest">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 am-dot" /> Detected: {osLabel(os)}
            </div>
          </div>
          <div className="lg:col-span-5 am-card p-5">
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">Notify me on release</div>
            <div className="flex gap-2">
              <input
                data-testid="download-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="flex-1 bg-[#0d0d0d] hairline px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#ff2a2a]"
              />
              <button
                data-testid="download-notify"
                onClick={() => {
                  if (!email || !/\S+@\S+\.\S+/.test(email)) return toast.error('Enter a valid email');
                  toast.success(`Thanks! We'll email ${email} on launch.`);
                  setEmail('');
                }}
                className="bg-[#ff2a2a] hover:bg-[#e01f1f] text-white text-sm font-semibold px-4 transition-colors"
              >
                Join
              </button>
            </div>
            <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-zinc-600">
              PIPEDA-compliant. Unsubscribe anytime.
            </div>
          </div>
        </div>
      </section>

      {/* Platforms */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
        <div className="grid md:grid-cols-2 gap-4" data-testid="platforms-grid">
          {PLATFORMS.map((p) => {
            const highlight = (os === 'ios' && p.id === 'ios') || (os === 'android' && p.id === 'android') || (os === 'windows' && p.id === 'windows') || ((os === 'web' || os === 'macos' || os === 'linux') && p.id === 'pwa');
            return (
              <div
                key={p.id}
                className={`am-card p-6 relative ${highlight ? 'border-[#ff2a2a]' : ''}`}
                data-testid={`platform-${p.id}`}
              >
                {highlight && (
                  <div className="absolute -top-3 left-6 bg-[#ff2a2a] text-white text-[10px] font-mono uppercase tracking-widest px-2 py-1">
                    Recommended for you
                  </div>
                )}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#0d0d0d] hairline grid place-items-center">
                      <p.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="font-display text-2xl uppercase tracking-tight leading-none">{p.name}</div>
                      <div className="text-xs text-zinc-500 mt-1">{p.sub}</div>
                    </div>
                  </div>
                  <span className={`text-[10px] font-mono uppercase tracking-widest border px-2 py-1 ${BadgeColor(p.status)}`}>
                    {p.badge}
                  </span>
                </div>
                <button
                  onClick={() => handleAction(p)}
                  data-testid={`platform-action-${p.id}`}
                  className={`mt-6 w-full inline-flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-colors ${
                    p.status === 'available' ? 'bg-[#ff2a2a] hover:bg-[#e01f1f] text-white'
                    : p.status === 'beta' ? 'bg-white text-black hover:bg-zinc-200'
                    : 'hairline text-white hover:border-[#ff2a2a]'
                  }`}
                >
                  <DL className="w-4 h-4"/> {p.action.label}
                </button>
              </div>
            );
          })}
        </div>

        {/* Instructions */}
        <div className="mt-16 grid md:grid-cols-3 gap-6" data-testid="install-instructions">
          <div className="am-card p-6">
            <Smartphone className="w-6 h-6 text-[#ff2a2a]"/>
            <div className="mt-3 font-display text-xl uppercase">On your phone</div>
            <ol className="mt-3 space-y-2 text-sm text-zinc-300 list-decimal list-inside">
              <li>Open automobile.direct in Safari or Chrome</li>
              <li>Tap Share → &ldquo;Add to Home Screen&rdquo;</li>
              <li>Launch AutoMobile like any native app</li>
            </ol>
          </div>
          <div className="am-card p-6">
            <Monitor className="w-6 h-6 text-[#ff2a2a]"/>
            <div className="mt-3 font-display text-xl uppercase">On Windows</div>
            <ol className="mt-3 space-y-2 text-sm text-zinc-300 list-decimal list-inside">
              <li>Download the .exe installer above</li>
              <li>Run installer (accept SmartScreen prompt)</li>
              <li>Pin to Start · sign in and go</li>
            </ol>
          </div>
          <div className="am-card p-6">
            <Car className="w-6 h-6 text-[#ff2a2a]"/>
            <div className="mt-3 font-display text-xl uppercase">In your vehicle</div>
            <ol className="mt-3 space-y-2 text-sm text-zinc-300 list-decimal list-inside">
              <li>Open your car&rsquo;s browser (Tesla, Rivian, Polestar)</li>
              <li>Navigate to automobile.direct</li>
              <li>Install PWA for a native infotainment experience</li>
            </ol>
          </div>
        </div>

        {/* Requirements */}
        <div className="mt-12 am-card p-6" data-testid="system-reqs">
          <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-4">System requirements</div>
          <div className="grid md:grid-cols-4 gap-6 text-sm">
            {[
              ['iOS', 'iOS 15+ · 120 MB'],
              ['Android', '8.0 (API 26)+ · 95 MB'],
              ['Windows', 'Win 10 1809+ · 250 MB'],
              ['PWA', 'Any modern browser'],
            ].map(([k, v]) => (
              <div key={k}>
                <div className="text-white font-semibold">{k}</div>
                <div className="text-zinc-400 mt-1">{v}</div>
              </div>
            ))}
          </div>
          <div className="mt-6 flex items-center gap-2 text-xs text-zinc-500">
            <Check className="w-3.5 h-3.5 text-emerald-400"/> PIPEDA compliant · GDPR-ready · Zero third-party trackers
          </div>
        </div>
      </section>
    </div>
  );
}
